import { filterDocumentsByOverview } from '../../utils/documents.overview';
import { canDownloadDocument, canPreviewDocument } from '../../utils';
import { DOCUMENT_CATEGORIES, DOCUMENT_STATUSES } from '../../utils/documents.constants';

export const documentsFilterConfig = {
  label: 'Filtri documenti',
  search: {
    key: 'query',
    placeholder: '',
    label: 'Cerca documento o cliente',
  },
  groups: [
    {
      key: 'overview', label: 'Riepilogo', selection: 'single', emptyValue: '',
      options: [
        { value: 'thisWeek', label: 'Questa settimana' },
        { value: 'toComplete', label: 'Da completare' },
        { value: 'overdue', label: 'Scaduti' },
      ],
    },
    {
      key: 'category',
      label: 'Tipo documento',
      selection: 'single',
      emptyValue: 'Tutti',
      options: DOCUMENT_CATEGORIES.map((category) => ({
        value: category,
        label: category,
      })),
    },
    {
      key: 'status',
      label: 'Stato',
      selection: 'multi',
      options: DOCUMENT_STATUSES.map((status) => ({
        value: status.value,
        label: status.label,
      })),
    },
  ],
};

export const DOCUMENT_STATUS_TONES = {
  in_revisione: { label: 'In Revisione', tone: 'review' },
  approvato: { label: 'Approvato', tone: 'approved' },
  firmato: { label: 'Firmato', tone: 'signed' },
  completato: { label: 'Completato', tone: 'completed' },
};

export function getDocumentStatusView(status) {
  return (
    DOCUMENT_STATUS_TONES[status] || {
      label: status || 'Sconosciuto',
      tone: 'neutral',
    }
  );
}

export function buildDocumentMetrics(documents = []) {

  return [
    {
      id: 'thisWeek',
      label: 'Questa settimana',
      value: filterDocumentsByOverview(documents, 'thisWeek').length,
      iconKey: 'metrics.month',
      tone: 'neutral',
    },
    {
      id: 'totalDocuments',
      label: 'Totale Documenti',
      value: documents.length,
      iconKey: 'metrics.document',
      tone: 'neutral',
    },
    {
      id: 'toComplete',
      label: 'Da completare',
      value: filterDocumentsByOverview(documents, 'toComplete').length,
      iconKey: 'metrics.pending',
      tone: 'progress',
    },
    {
      id: 'overdue',
      label: 'Scaduti',
      value: filterDocumentsByOverview(documents, 'overdue').length,
      iconKey: 'metrics.overdue',
      tone: 'attention',
    },
  ];
}

export function getClientResolutionHint(status) {
  if (status === 'legacy_fallback') return 'dato legacy';
  if (status === 'missing_client_id') return 'clientId mancante';
  if (status === 'unresolved_client_reference') return 'clientId non risolto';
  return '';
}

export function buildDocumentColumns({ StatusComponent }) {
  return [
    {
      id: 'name',
      render: (document) => <span className="documents-table__name">{document.name}</span>,
    },
    {
      id: 'clientId',
      render: (document) => {
        const clientName = document.clientResolvedName || document.client || 'Cliente non risolto';
        const clientHint = getClientResolutionHint(document.clientResolutionStatus);

        return (
          <span className="documents-table__client">
            <span>{clientName}</span>
            {clientHint ? <small>{clientHint}</small> : null}
          </span>
        );
      },
    },
    {
      id: 'status',
      render: (document) => <StatusComponent status={document.status} />,
    },
  ];
}

function getPreviewDisabledTitle(document, documentsSnapshotStale) {
  if (documentsSnapshotStale || document?.isSnapshotStale === true) {
    return 'Anteprima non disponibile da snapshot locale non aggiornato';
  }

  if (!document?.id) {
    return 'Anteprima disponibile solo per documenti salvati nel backend';
  }

  return 'Anteprima documento non disponibile';
}

export function buildDocumentRowActions({
  onPreview,
  onViewNotes,
  onEdit,
  onDownload,
  onDelete,
  canManageDocuments,
  documentsSnapshotStale,
  previewingDocumentId,
}) {
  return (document) => {
    const previewEnabled = canPreviewDocument(document);
    const downloadEnabled = canDownloadDocument(document);
    const previewLoading =
      Boolean(previewingDocumentId) &&
      String(previewingDocumentId) ===
        String(document.id || document.previewUrl || document.fileName || 'preview');

    const actions = [
      {
        id: 'preview',
        iconKey: 'actions.preview',
        label: previewLoading ? 'Caricamento anteprima' : 'Anteprima documento',
        title: previewEnabled
          ? previewLoading
            ? 'Caricamento anteprima'
            : 'Anteprima documento'
          : getPreviewDisabledTitle(document, documentsSnapshotStale),
        disabled: !previewEnabled || previewLoading,
        onClick: () => previewEnabled && !previewLoading && onPreview?.(document),
      },
      {
        id: 'notes',
        iconKey: 'actions.notes',
        label: 'Visualizza commenti documento',
        title:
          document.isSnapshotStale === true
            ? 'Commenti non disponibili da snapshot locale non aggiornato'
            : 'Visualizza commenti documento',
        disabled: document.isSnapshotStale === true || !document.id,
        onClick: () => document.isSnapshotStale !== true && document.id && onViewNotes?.(document),
      },
      {
        id: 'edit',
        iconKey: 'actions.edit',
        label: 'Modifica metadata documento',
        title:
          document.isSnapshotStale === true
            ? 'Modifica non disponibile da snapshot locale non aggiornato'
            : 'Modifica metadata documento',
        disabled: document.isSnapshotStale === true,
        onClick: () => document.isSnapshotStale !== true && onEdit?.(document),
      },
      {
        id: 'download',
        iconKey: 'actions.download',
        label: 'Scarica documento',
        title: downloadEnabled
          ? 'Scarica documento'
          : documentsSnapshotStale
            ? 'Download non disponibile da snapshot locale non aggiornato'
            : 'Download documento non disponibile',
        disabled: !downloadEnabled,
        onClick: () => downloadEnabled && onDownload?.(document),
      },
    ];

    if (canManageDocuments) {
      actions.push({
        id: 'delete',
        iconKey: 'actions.delete',
        label: 'Elimina documento',
        title: 'Elimina documento',
        tone: 'danger',
        onClick: () => onDelete?.(document),
      });
    }

    return actions;
  };
}
